import { create } from 'zustand';
import { AvailableCarrierItem, Order } from '@/types';

export interface SelectedCarrier {
  carrier_slug: string;
  carrier_name: string;
  carrier_logo_url: string;
  shipping_fee: number;
  delivery_day: 'same' | 'next';
  same_day_cutoff: string | null;
  drop_off_time: string | null;
}

interface CarrierSelectionData {
  process_id: string;
  recommended_carrier_slug: string;
  available_carriers: AvailableCarrierItem[];
}

export interface ContactDisplayInfo {
  provinceName: string;
  areaName: string;
}

// RS-23 — auto-dispatch decision context surfaced to the carrier-selection
// step so we can render a transparency banner ("Auto-assigned to X by rule Y").
export interface RuleDispatchContext {
  action: 'auto-dispatched' | 'auto-cheapest' | 'restricted' | 'manual';
  winningRuleUid: string | null;
  // RS Ph3 OVR Phase 2 — when the rule auto-dispatched a carrier, capture
  // its uid + slug so SelectCarrier can detect when the merchant picks a
  // different carrier and trigger the override-reason modal.
  assignedCarrierUid?: string | null;
  assignedCarrierSlug?: string | null;
}

interface NewOrderStore {
  orderPayload: Order | null;
  selectedCarrier: SelectedCarrier | null;
  carrierSelection: CarrierSelectionData | null;
  sourceRegionUid: string | null;
  contactDisplayInfo: ContactDisplayInfo | null;
  savedProvinceCode: string | null;
  ruleDispatch: RuleDispatchContext | null;
  isEditMode: boolean;
  editOrderUid: string | null;
  setOrderPayload: (payload: Order) => void;
  setSelectedCarrier: (carrier: SelectedCarrier | null) => void;
  setCarrierSelection: (data: CarrierSelectionData | null) => void;
  setSourceRegionUid: (uid: string) => void;
  setContactDisplayInfo: (info: ContactDisplayInfo) => void;
  setSavedProvinceCode: (code: string | null) => void;
  setRuleDispatch: (ctx: RuleDispatchContext | null) => void;
  setEditMode: (orderUid: string) => void;
  clearEditMode: () => void;
  reset: () => void;
}

const useNewOrderStore = create<NewOrderStore>((set) => ({
  orderPayload: null,
  selectedCarrier: null,
  carrierSelection: null,
  sourceRegionUid: null,
  contactDisplayInfo: null,
  savedProvinceCode: null,
  ruleDispatch: null,
  isEditMode: false,
  editOrderUid: null,
  setOrderPayload: (payload) => set({ orderPayload: payload }),
  setSelectedCarrier: (carrier) => set({ selectedCarrier: carrier }),
  setCarrierSelection: (data) => set({ carrierSelection: data }),
  setSourceRegionUid: (uid) => set({ sourceRegionUid: uid }),
  setContactDisplayInfo: (info) => set({ contactDisplayInfo: info }),
  setSavedProvinceCode: (code) => set({ savedProvinceCode: code }),
  setRuleDispatch: (ctx) => set({ ruleDispatch: ctx }),
  setEditMode: (orderUid) => set({ isEditMode: true, editOrderUid: orderUid }),
  clearEditMode: () => set({ isEditMode: false, editOrderUid: null }),
  reset: () => set({ orderPayload: null, selectedCarrier: null, carrierSelection: null, sourceRegionUid: null, contactDisplayInfo: null, savedProvinceCode: null, ruleDispatch: null, isEditMode: false, editOrderUid: null }),
}));

export default useNewOrderStore;
